import { config } from '@/config';
import type { IHttpClient, RequestInterceptor, ResponseInterceptor } from './types/httpClient.interface';
import { AxiosAdapter } from './adapters/axiosAdapter';

/**
 * HTTP Client Service (Framework Agnostic)
 * 
 * This service provides a clean abstraction over HTTP operations.
 * It uses the IHttpClient interface, making it easy to swap implementations
 * (e.g., from Axios to Fetch, ky, or any other HTTP library)
 */
export class HttpClientService {
  private _client: IHttpClient;

  constructor(httpClient?: IHttpClient) {
    // Use provided client or default to Axios adapter
    this._client = httpClient || new AxiosAdapter(
      config.apiUrl,
      {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }
    );
  }

  get client(): IHttpClient {
    return this._client;
  }

  /**
   * Add a request interceptor
   * @returns Interceptor ID for later removal
   */
  addRequestInterceptor(interceptor: RequestInterceptor): number {
    return this._client.addRequestInterceptor(interceptor);
  }

  /**
   * Add a response interceptor
   * @returns Interceptor ID for later removal
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): number {
    return this._client.addResponseInterceptor(interceptor);
  }

  /**
   * Remove a request interceptor by ID
   */
  removeRequestInterceptor(id: number): void {
    this._client.removeRequestInterceptor(id);
  }

  /**
   * Remove a response interceptor by ID
   */
  removeResponseInterceptor(id: number): void {
    this._client.removeResponseInterceptor(id);
  }

  /**
   * Create an abort controller for canceling requests
   */
  createAbortController(): AbortController {
    return this._client.createAbortController();
  }

  // Legacy compatibility - deprecated
  /** @deprecated Use addRequestInterceptor instead */
  setInterceptor(config: { type: 'request' | 'response'; onFulfilled: any; onRejected?: any }) {
    if (config.type === 'request') {
      this.addRequestInterceptor({ onFulfilled: config.onFulfilled });
    } else if (config.type === 'response') {
      this.addResponseInterceptor({ 
        onFulfilled: config.onFulfilled,
        onRejected: config.onRejected
      });
    }
  }

  /** @deprecated Use createAbortController instead */
  generateCancelTokenSource() {
    // For backward compatibility - return an object with cancel method
    const controller = this.createAbortController();
    return {
      token: controller.signal,
      cancel: () => controller.abort(),
    };
  }
}
